import { Injectable } from '@angular/core';
import { Apollo as BaseApollo, QueryRef as BaseQueryRef } from 'apollo-angular';
import { Observable } from 'rxjs';
import { filter, shareReplay } from 'rxjs/operators';
import { WatchQueryOptions, MutationOptions, FetchResult, SubscriptionOptions } from '@apollo/client/core';

/**
 * Extended QueryRef that automatically filters Apollo 4 loading states
 *
 * Wraps the standard QueryRef to override valueChanges with automatic filtering
 */
class FilteredQueryRef<T, V = any> {
  private _filteredValueChanges: Observable<any>;

  constructor(private originalQueryRef: BaseQueryRef<T, V>) {
    // Drop only the empty initial loading emission so cache-and-network
    // can deliver { loading: true, data: <cached> } for instant first paint.
    this._filteredValueChanges = this.originalQueryRef.valueChanges.pipe(
      filter((result: any) => {
        if (result.loading && !result.data && !result.error && !(result.errors && result.errors.length > 0)) {
          return false;
        }
        return true;
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );
  }

  // Override valueChanges to return filtered Observable
  get valueChanges(): Observable<any> {
    return this._filteredValueChanges;
  }

  // Proxy all other QueryRef methods to the original
  refetch = (variables?: V) => this.originalQueryRef.refetch(variables);
  fetchMore = (options: any) => this.originalQueryRef.fetchMore(options);
  updateQuery = (mapFn: any) => this.originalQueryRef.updateQuery(mapFn);
  stopPolling = () => this.originalQueryRef.stopPolling();
  startPolling = (pollInterval: number) => this.originalQueryRef.startPolling(pollInterval);
  subscribeToMore = (options: any) => this.originalQueryRef.subscribeToMore(options);
  setVariables = (variables: V) => this.originalQueryRef.setVariables(variables);
  getCurrentResult = () => this.originalQueryRef.getCurrentResult();

  get variables() {
    return this.originalQueryRef.variables;
  }
  get options() {
    return this.originalQueryRef.options;
  }
}

/**
 * Global Apollo Service Wrapper
 *
 * Automatically handles Apollo 4 loading state behavior across the entire application.
 * All watchQuery calls return QueryRefs with filtered valueChanges that skip loading states.
 *
 * This service is provided globally via GraphQLModule and replaces the standard Apollo service.
 */
@Injectable()
export class Apollo extends BaseApollo {
  /**
   * Create a watchQuery with automatic loading state filtering
   *
   * Returns a QueryRef where valueChanges automatically filters out
   * { loading: true, data: undefined } emissions.
   */
  override watchQuery<T, V = any>(options: WatchQueryOptions<V, T>): any {
    const originalQueryRef = super.watchQuery<T, V>(options);
    return new FilteredQueryRef<T, V>(originalQueryRef);
  }

  // No need to override query, mutate, subscribe - they work fine as-is
}
